/* eslint-disable camelcase */
import { createReducer, createSelector } from '@reduxjs/toolkit';
import { parseDate, getLocalTimeZone, today } from '@internationalized/date';
import { Location } from 'history';

import * as jobTypes from './../flatJobs/types';
import {
  addBuild,
  fetchFilters,
  fetchTeam,
  fetchTeams,
  fetchUser,
  fetchUsers,
  searchByField,
} from './actions';
import { User, Team } from './api';
import {
  DateRange,
  Environments,
  Environment,
  OwnerItem,
  Status,

  isEnvironment,
  isOwnerItem,
  isStatus,
  isRelativeDateRange,
  toCalendarDate,
  toRelativeDateRange,
} from './types';
import { compare } from './utils/sort';
import * as convert from './utils/convert';
import { selectUser } from '../users/selectors';

type FetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

const selectUsers = (state): User[] => Object.values(state.apps.jobs.filterBar.domain.users.byId);
const selectTeams = (state): Team[] => Object.values(state.apps.jobs.filterBar.domain.teams.byId);
const selectFilterData = (state) => state.apps.jobs.filterBar.domain.filters as FilterBarFilters;
const selectEnvironmentSelection = createSelector(
  [
    (state: any) => state.router.location.search as string,
  ],
  (locationString) => {
    const params = new URLSearchParams(locationString);
    // Note: In the case there is no value present in the URL, we'll present the one
    // stored in localStorage.
    const urlEnv = params.get('environment');
    if (urlEnv && isEnvironment(urlEnv)) {
      return urlEnv;
    }

    return null;
  },
);

export interface FilterSelection {
  environment: Environment | null;
  statuses: Status[];
  owner: OwnerItem | null;
  search: string;
  dateRange: DateRange | null;
  builds: {
    [key in Environment]: string[];
  };
  platforms: {
    [key in Environment]: string[];
  };
  browsers: {
    [key in Environment]: string[];
  };
  devices: {
    [key in Environment]: string[];
  };
  tags: {
    [key in Environment]: string[];
  };
}

export interface FilterStatus {
  user: FetchStatus;
  users: FetchStatus;
  team: FetchStatus;
  teams: FetchStatus;
  filters: {
    [key in Environment]: FetchStatus;
  };
}

interface FilterBarState {
  domain: {
    users: {
      byId: {
        [key: string]: User;
      };
    };
    teams: {
      byId: {
        [key: string]: Team;
      };
    };
    filters: FilterBarFilters;
  },
  app: {
    status: FilterStatus;
  };
}

export type FilterBarFilters = {
  [key in Environment]: {
    build_name: {
      byValue: { [key: string]: number };
    };
    platform: {
      byName: {
        [key: string]: string[];
      };
    };
    browser: {
      byName: {
        [key: string]: string[];
      };
    };
    device: {
      byValue: { [key: string]: number };
    };
    tags: {
      byValue: { [key: string]: number };
    };
  };
};

const initialState = {
  domain: {
    users: {
      byId: {},
    },
    teams: {
      byId: {},
    },
    filters: {
      vdc: {
        build_name: {
          byValue: {},
        },
        platform: {
          byName: {},
        },
        browser: {
          byName: {},
        },
        device: {
          byValue: {},
        },
        tags: {
          byValue: {},
        },
      },
      rdc: {
        build_name: {
          byValue: {},
        },
        platform: {
          byName: {},
        },
        browser: {
          byName: {},
        },
        device: {
          byValue: {},
        },
        tags: {
          byValue: {},
        },
      },
    },
  },
  app: {
    status: {
      user: 'idle',
      users: 'idle',
      team: 'idle',
      teams: 'idle',
      filters: {
        rdc: 'idle',
        vdc: 'idle',
      },
    },
  },
} as FilterBarState;

const filterBarReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(fetchUser.pending, (draft) => {
      draft.app.status.user = 'loading';
    })
    .addCase(fetchUser.fulfilled, (draft, action) => {
      draft.app.status.user = 'succeeded';
      draft.domain.users.byId[action.payload.id] = action.payload;
    })
    .addCase(fetchUser.rejected, (draft) => {
      draft.app.status.user = 'failed';
    })
    .addCase(fetchTeam.pending, (draft) => {
      draft.app.status.team = 'loading';
    })
    .addCase(fetchTeam.fulfilled, (draft, action) => {
      draft.app.status.team = 'succeeded';
      draft.domain.teams.byId[action.payload.id] = action.payload;
    })
    .addCase(fetchTeam.rejected, (draft) => {
      draft.app.status.team = 'failed';
    })
    .addCase(fetchUsers.pending, (draft) => {
      draft.app.status.users = 'loading';
    })
    .addCase(fetchUsers.rejected, (draft) => {
      draft.app.status.users = 'failed';
    })
    .addCase(fetchUsers.fulfilled, (draft, action) => {
      action.payload.results.forEach((user) => {
        draft.domain.users.byId[user.id] = user;
      });

      draft.app.status.users = 'succeeded';
    })
    .addCase(fetchTeams.pending, (draft) => {
      draft.app.status.teams = 'loading';
    })
    .addCase(fetchTeams.rejected, (draft) => {
      draft.app.status.teams = 'failed';
    })
    .addCase(fetchTeams.fulfilled, (draft, action) => {
      action.payload.results.forEach((team) => {
        draft.domain.teams.byId[team.id] = team;
      });
      draft.app.status.teams = 'succeeded';
    })
    .addCase(fetchFilters.pending, (draft, action) => {
      const { source } = action.meta.arg;
      draft.app.status.filters[source] = 'loading';
    })
    .addCase(fetchFilters.rejected, (draft, action) => {
      const { source } = action.meta.arg;
      draft.app.status.filters[source] = 'failed';
    })
    .addCase(fetchFilters.fulfilled, (draft, action) => {
      const { source } = action.meta.arg;
      draft.app.status.filters[source] = 'succeeded';

      action.payload.build_name.forEach((item) => {
        if (!item.value) {
          return;
        }
        draft.domain.filters[source].build_name.byValue[item.value] = item.count ?? 0;
      });
      action.payload.tag.forEach((item) => {
        if (!item.value) {
          return;
        }
        draft.domain.filters[source].tags.byValue[item.value] = item.count ?? 0;
      });
      action.payload.os.forEach((item) => {
        const platform = item.value;
        const versionList = item.versions;
        if (platform === '') {
          return;
        }

        versionList.forEach((version) => {
          const platformsByName = draft.domain.filters[source].platform.byName;
          if (!(platform in platformsByName)) {
            platformsByName[platform] = [];
          }
          if (!platformsByName[platform].includes(version.value)) {
            platformsByName[platform].push(version.value);
          }
        });
      });
      action.payload.browser.forEach((item) => {
        const browser = item.value;
        const versionList = item.versions;
        if (browser === '') {
          return;
        }
        versionList.forEach((version) => {
          const browsersByName = draft.domain.filters[source].browser.byName;
          if (!(browser in browsersByName)) {
            browsersByName[browser] = [];
          }
          if (!browsersByName[browser].includes(version.value)) {
            browsersByName[browser].push(version.value);
          }
        });
      });
      action.payload.device.forEach((item) => {
        const device = item.value;
        if (device === '') {
          return;
        }
        draft.domain.filters[source].device.byValue[`${device}`] = item.count;
      });
    })
    .addCase(searchByField.fulfilled, (draft, action) => {
      const { source, field } = action.meta.arg;
      if (!source) {
        return;
      }

      // eslint-disable-next-line default-case
      switch (field) {
        case 'tag':
          action.payload.items.forEach((item) => {
            draft.domain.filters[source].tags.byValue[`${item}`] = 1;
          });
          break;
        case 'build':
          action.payload.items.forEach((item) => {
            draft.domain.filters[source].build_name.byValue[`${item}`] = 1;
          });
          break;
      }
    })
    .addCase(addBuild, (draft, action) => {
      const { environment, values } = action.payload;
      values.forEach((val) => {
        if (val in draft.domain.filters[environment].build_name.byValue) {
          return;
        }
        draft.domain.filters[environment].build_name.byValue[val] = 1;
      });
    });
});

export const selectors = {
  selectFilterSelection: createSelector([
    (state: any): Location => state.router.location as Location,
    selectEnvironmentSelection,
  ], (location, environment) => {
    const params = new URLSearchParams(location.search);

    let buildsVdc: string[] = [];
    const urlBuildVdc = params.get('buildVdc');
    if (urlBuildVdc) {
      buildsVdc = urlBuildVdc.split(',');
    }
    let buildsRdc: string[] = [];
    const urlBuildRdc = params.get('buildRdc');
    if (urlBuildRdc) {
      buildsRdc = urlBuildRdc.split(',');
    }

    // Note: If no user select, defaults to current user
    let owner: OwnerItem | null = null;
    const urlOwnerId = params.get('ownerId');
    const urlOwnerType = params.get('ownerType');
    const ownerMaybe = { id: urlOwnerId, name: '', type: urlOwnerType };
    if (urlOwnerId && urlOwnerType && isOwnerItem(ownerMaybe)) {
      owner = ownerMaybe;
    }

    let platformsVdc: string[] = [];
    const urlPlatformVdc = params.get('platformVdc');
    if (urlPlatformVdc) {
      platformsVdc = urlPlatformVdc.split(',');
    }

    let platformsRdc: string[] = [];
    const urlPlatformRdc = params.get('platformRdc');
    if (urlPlatformRdc) {
      platformsRdc = urlPlatformRdc.split(',');
    }

    let browsersVdc: string[] = [];
    const urlBrowsersVdc = params.get('browserVdc');
    if (urlBrowsersVdc) {
      browsersVdc = urlBrowsersVdc.split(',');
    }

    let devicesVdc: string[] = [];
    const urlDevicesVdc = params.get('deviceVdc');
    if (urlDevicesVdc) {
      devicesVdc = urlDevicesVdc.split(',');
    }

    let devicesRdc: string[] = [];
    const urlDevicesRdc = params.get('deviceRdc');
    if (urlDevicesRdc) {
      devicesRdc = urlDevicesRdc.split(',');
    }

    const tagVdc: string[] = [];
    const urlTagVdc = params.get('tagVdc');
    if (urlTagVdc) {
      tagVdc.push(...urlTagVdc.split(','));
    }

    const tagRdc: string[] = [];
    const urlTagRdc = params.get('tagRdc');
    if (urlTagRdc) {
      tagRdc.push(...urlTagRdc.split(','));
    }

    let searchValue = '';
    const urlSearch = params.get('search');
    if (urlSearch) {
      searchValue = urlSearch;
    }

    let statuses: Status[] = [];
    const urlStatus = params.get('status');
    if (urlStatus) {
      statuses = urlStatus.split(',').filter((x) => isStatus(x)) as Status[];
    }

    let dateRange: DateRange | undefined;
    try {
      const start = params.get('start');
      const end = params.get('end');

      if (isRelativeDateRange(start)) {
        dateRange = start;
      } else if (start && end) {
        dateRange = {
          start: parseDate(start),
          end: parseDate(end),
        };
      }
    } catch {
      // NOTE: If start and end are unparseable, leave date range selection empty
      // and let the consumer set a default.
    }

    const selection = {
      environment,
      builds: {
        rdc: buildsRdc,
        vdc: buildsVdc,
      },
      owner,
      platforms: {
        rdc: platformsRdc,
        vdc: platformsVdc,
      },
      browsers: {
        rdc: [], // we never filter RDC by browsers
        vdc: browsersVdc,
      },
      devices: {
        rdc: devicesRdc,
        vdc: devicesVdc,
      },
      tags: {
        rdc: tagRdc,
        vdc: tagVdc,
      },
      search: searchValue,
      statuses,
      dateRange,
    } as FilterSelection;

    return selection;
  }),

  selectFilterStatus: (state): FilterStatus => (
    state.apps.jobs.filterBar.app.status as FilterStatus
  ),

  selectAllUsers: (state): User[] => (
    Object.values(state.apps.jobs.filterBar.domain.users.byId)
  ),

  selectAllUsersExceptMine: createSelector(
    [selectUser, selectUsers],
    (currentUser, users) => users
      .filter((user) => user.id !== currentUser.user_id)
      .sort((userA, userB) => {
        // Handle undefined names.
        if (!userA.username || !userB.username) {
          return 0;
        }

        return userA.username.localeCompare(userB.username);
      }),
  ),

  selectAllTeams: (state): Team[] => (
    Object.values(state.apps.jobs.filterBar.domain.teams.byId)
  ),

  selectAllTeamsExceptMine: createSelector(
    [selectUser, selectTeams],
    (currentUser, teams) => teams
      .filter((team) => team.id !== currentUser.getUserTeamId())
      .sort((teamA, teamB) => {
        // Handle undefined names.
        if (!teamA.name || !teamB.name) {
          return 0;
        }

        return teamA.name.localeCompare(teamB.name);
      }),
  ),

  selectIsFiltered: (state, currentUserId: string): boolean => {
    const {
      dateRange,
      owner,
      statuses,
      search: searchQuery,
      builds,
      platforms,
      environment,
      browsers,
      devices,
      tags,
    } = selectors.selectFilterSelection(state);
    // NOTE: The ui makes a distinction between when the currently logged in user has
    // no tests and when a filtered search returns no tests.
    const isOwnerFiltered = owner && owner.type === 'user' && owner.id !== currentUserId;

    let isDefaultDateRange = true;
    if (dateRange) {
      const rel = toRelativeDateRange(dateRange);
      isDefaultDateRange = rel === 'last7days';
    }

    return (
      isOwnerFiltered ||
      !isDefaultDateRange ||
      statuses.length > 0 ||
      searchQuery !== '' ||
      builds[environment ?? Environments.VDC].length > 0 ||
      platforms[environment ?? Environments.VDC].length > 0 ||
      browsers[environment ?? Environments.VDC].length > 0 ||
      devices[environment ?? Environments.VDC].length > 0 ||
      tags[environment ?? Environments.VDC].length > 0
    );
  },

  selectBuildItems: createSelector(
    [
      selectFilterData,
      selectEnvironmentSelection,
    ],
    (filters, env) => {
      if (env) {
        return Object.keys(filters[env].build_name.byValue)
          .sort(compare.caseInsensitiveStrings)
          .map((build) => ({
            key: build,
          }));
      }
      return [];
    },
  ),

  selectPlatformItems: createSelector(
    [
      selectFilterData,
      selectEnvironmentSelection,
    ],
    (filters, env) => {
      const items: {
        key: string,
        children: {
          key: string,
          name: string,
          version: string
        }[]
      }[] = [];

      if (!env) {
        return items;
      }

      const platforms = Object.keys(filters[env].platform.byName)
        .sort();
      platforms.forEach((name) => {
        const versions = [...filters[env].platform.byName[name]]
          .map(name === 'macOS' ? convert.macNameToVersion : (val) => val)
          .sort(compare.rsemver)
          .map(name === 'macOS' ? convert.macVersionToName : (val) => val);

        items.push({
          key: name,
          children: versions.map((version) => ({
            key: `${name} ${version}`,
            name,
            version,
          })),
        });
      });
      return items;
    },
  ),

  selectBrowserItems: createSelector(
    [
      selectFilterData,
      selectEnvironmentSelection,
    ],
    (filters, env) => {
      const items: {
        key: string,
        children: {
          key: string,
          name: string,
          version: string
        }[]
      }[] = [];

      if (!env) {
        return items;
      }

      const browsers = Object.keys(filters[env].browser.byName).sort();
      browsers.forEach((name) => {
        const versions = [...filters[env].browser.byName[name]]
          .sort(compare.rsemver);

        items.push({
          key: name,
          children: versions.map((version) => ({
            key: `${name} ${version}`,
            name,
            version,
          })),
        });
      });
      return items;
    },
  ),

  selectDeviceItems: createSelector(
    [
      selectFilterData,
      selectEnvironmentSelection,
    ],
    (filters, env) => {
      if (env) {
        return Object.keys(filters[env].device.byValue)
          .sort(compare.caseInsensitiveStrings)
          .map((device) => ({
            key: device,
          }));
      }
      return [];
    },
  ),

  selectTagItems: createSelector(
    [
      selectFilterData,
      selectEnvironmentSelection,
    ],
    (filters, env) => {
      if (env) {
        return Object.keys(filters[env].tags.byValue)
          .filter((tag) => tag !== '')
          .sort(compare.caseInsensitiveStrings)
          .map((tag) => ({
            key: tag,
          }));
      }
      return [];
    },
  ),

  selectLocation: (state): Location<unknown> => state.router.location as Location<unknown>,

  /**
   * Return the filter bar date range as native JS Dates with times set to midnight
   * in the local timezone.
   */
  selectJSDateRange: createSelector(
    [
      (state: any): string => state.router.location.search as string,
    ],
    (locationSearch) => {
      const params = new URLSearchParams(locationSearch);
      try {
        const start = params.get('start');
        const end = params.get('end');
        const localTZ = getLocalTimeZone();
        if (isRelativeDateRange(start)) {
          return {
            start: toCalendarDate(start).toDate(localTZ),
            end: today(localTZ).toDate(localTZ),
          };
        }
        if (start && end) {
          return {
            start: parseDate(start).toDate(localTZ),
            end: parseDate(end).toDate(localTZ),
          };
        }
      } catch {
        // NOTE: If start and end are unparseable, leave date range selection empty
        // and let the consumer set a default.
      }
    },
  ),
};

export default filterBarReducer;
